import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  BackButton,
  CancelButton,
  SubmitButton,
} from "../../../../Components/Utils/Buttons";
import useAddFormData from "../../../../Components/Hooks/useAddFormData";
import {
  NuDate,
  NuInput,
  NuTextArea,
  NuUpload,
} from "../../../../Components/Utils/Input";
import FormikSelectTwo from "../../../../Components/Utils/Select";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import UseDropDown from "../../../../Components/Hooks/useDropDown";

const CreateContracts = () => {
  const { Oncancel, addFormData, contextHolder, isLoading } = useAddFormData();
  const [image, setImage] = useState({ preview: "", raw: "" });
  const [createNew] = useState({
    customerId: "",
    contractTitle: "",
    scopeOfWork: "",
    contractAmount: "",
    additionalAmount: "",
    totalAmount: "",
    signedOn: "",
    projectDuration: "",
    startDate: "",
    endDate: "",
    comments: "",
    receivables: "",
  });

  const handleChangeImg = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage({
        preview: URL.createObjectURL(e.target.files[0]),
        raw: e.target.files[0],
      });
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: createNew,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: yup.object({
      customerId: yup.string().required("Customer is required!"),
      contractTitle: yup.string().required("Contract Title is required!"),
      contractAmount: yup
        .number()
        .typeError("Contract Amount must be a number")
        .nullable(),
      additionalAmount: yup
        .number()
        .typeError("Additional Amount must be a number")
        .nullable(),
    }),
    onSubmit: async (value, { resetForm }) => {
      const datas = {
        ...value,
        projectDuration: daysDifference !== "" ? daysDifference : 0,
        totalAmount: totalAmount || 0,
      };
      let formData = new FormData();
      formData.append("contractDetails", JSON.stringify(datas));
      if (image.raw) {
        formData.append("attachments", image.raw);
      }
      addFormData("/contracts/new-contract", formData, resetForm);
    },
  });

  const daysDifference = useMemo(() => {
    if (!formik?.values?.startDate || !formik?.values?.endDate) return "";
    const startDate = new Date(formik.values.startDate);
    const endDate = new Date(formik.values.endDate);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return "";
    }

    const differenceInMs = endDate.getTime() - startDate.getTime();
    const diffInDays = Math.ceil(differenceInMs / (1000 * 60 * 60 * 24));
    return diffInDays >= 0 ? diffInDays : 0;
  }, [formik?.values?.startDate, formik?.values?.endDate]);

  const totalAmount = useMemo(() => {
    const contractAmount = parseFloat(formik?.values?.contractAmount) || 0;
    const additionalAmount = parseFloat(formik?.values?.additionalAmount) || 0;
    return contractAmount + additionalAmount;
  }, [formik?.values?.contractAmount, formik?.values?.additionalAmount]);

  const { data: customer } = UseDropDown("/customers/drop-down");

  return (
    <div className="w-full h-full p-2 lg:px-4">
      <div className="flex justify-between items-center mb-4">
        <Breadcrumb
          items={[
            { name: "Procurement" },
            {
              name: "Contracts",
              link: "/procurement/contracts",
            },
            {
              name: "Create Contracts",
            },
          ]}
        />
        <BackButton />
      </div>

      <div className="w-full h-[calc(100vh-140px)] overflow-y-auto">
        <form onSubmit={formik.handleSubmit}>
          <div className="w-full mx-auto px-2 pb-4 bg-white rounded-md">
            <div className="w-full">
              <p className="py-2 px-4 text-xs 2xl:text-sm font-medium text-[#111537] underline decoration-[#048178] decoration-2 underline-offset-4">
                Contract Details :
              </p>
            </div>

            <div className="w-full flex justify-start content-start flex-wrap">
              <FormikSelectTwo
                label="Customer"
                placeholder="Choose Customer"
                Options={customer}
                formik={formik}
                name="customerId"
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuInput
                type="text"
                label="Contract Title"
                placeholder="Enter Contract Title"
                formik={formik}
                name="contractTitle"
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuInput
                type="text"
                label="Scope Of Work"
                placeholder="Enter Scope Of Work"
                formik={formik}
                name="scopeOfWork"
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuDate
                label="Signed On"
                placeholder="dd/mm/yyyy"
                formik={formik}
                name="signedOn"
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuDate
                label="Start Date"
                placeholder="dd/mm/yyyy"
                formik={formik}
                name="startDate"
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuDate
                label="End Date"
                placeholder="dd/mm/yyyy"
                formik={formik}
                name="endDate"
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuInput
                type="text"
                label="Project Duration (Days)"
                placeholder="Calculated automatically"
                value={daysDifference !== "" ? `${daysDifference} days` : ""}
                formik={formik}
                name="projectDuration"
                width="w-full md:w-1/2 lg:w-1/3"
                disabled={true}
              />

              <NuInput
                type="number"
                label="Contract Amount"
                placeholder="Enter Contract Amount"
                formik={formik}
                name="contractAmount"
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuInput
                type="number"
                label="Additional Amount"
                placeholder="Enter Additional Amount"
                formik={formik}
                name="additionalAmount"
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuInput
                type="number"
                label="Total Amount"
                placeholder="Total Amount"
                formik={formik}
                value={totalAmount}
                name="totalAmount"
                width="w-full md:w-1/2 lg:w-1/3"
                disabled={true}
              />

              <NuInput
                type="text"
                label="Receivables"
                placeholder="Enter Receivables"
                formik={formik}
                name="receivables"
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuTextArea
                label="Comments"
                placeholder="Enter Comments"
                formik={formik}
                name="comments"
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuUpload
                label="Attachment"
                width="w-full md:w-1/2 lg:w-1/3"
                onChange={handleChangeImg}
              />
            </div>

            <div className="w-full flex justify-end gap-4 p-4 mt-6">
              <CancelButton
                handleClick={() => {
                  Oncancel(-1);
                }}
              />
              <SubmitButton isLoading={isLoading} />
            </div>
          </div>
        </form>
      </div>
      {contextHolder}
    </div>
  );
};

export default CreateContracts;
